import { type ChangeEvent, type FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { hideInputLabelClass } from '@eneco/custom-components/flows/SummaryPaymentDetails.css';
import { Box, InputText, Stack, Text, TextLink } from '@eneco/sparky';
import { friendlyFormatIBAN } from 'ibantools';
import type { FieldError, UseFormRegister } from 'react-hook-form';

import type { SummaryPaymentDetailsTextualData } from '../../../../shared/components/SummaryStep/summary-step.types';

interface Props {
  textualData?: SummaryPaymentDetailsTextualData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  ibanError?: FieldError;
}

const formatIbanOnChange = (event: ChangeEvent<HTMLInputElement>) => {
  const target = event.target;
  const caretPosition = target.selectionStart;
  const currentValue = target.value;
  const newValue = friendlyFormatIBAN(currentValue) ?? '';

  target.value = newValue;
  target.selectionEnd = currentValue.length !== newValue.length ? (caretPosition ?? 0) + 1 : caretPosition;
};

/** IBAN field with the dialog trigger on the same row as the label, and a full-width input below. */
export const SummaryIbanField: FC<Props> = ({ textualData, register, ibanError }) => {
  const label = textualData?.ibanFormField?.value?.label ?? '';

  return (
    <Stack as="li" gap="2">
      <Stack direction="row" gap="4" alignX="justify" alignY="center">
        <Text size="BodyS" weight="bold">
          {label}
        </Text>
        <TrackedDialog
          title={textualData?.ibanDialog?.value?.title}
          trigger={<TextLink color="textLowEmphasis">{textualData?.ibanDialog?.value?.triggerText}</TextLink>}>
          <RichText html={textualData?.ibanDialog?.value?.content} />
        </TrackedDialog>
      </Stack>
      <Box className={hideInputLabelClass}>
        <InputText
          {...register('iban', { onChange: formatIbanOnChange })}
          hint={textualData?.ibanFormField?.value?.hint}
          label={label}
          error={ibanError?.message}
        />
      </Box>
    </Stack>
  );
};
